






















  import { Component, mixins } from 'nuxt-property-decorator'
  import { AsyncComponent } from 'vue'

  import { namespace } from 'vuex-class'
  import { Categories, CMS, Plugins } from '@one/types'
  import SeoMethodsMixin, { DIVIDER } from '../mixins/SeoMethodsMixin'
  import Category = Categories.Category;
  import StaticPageDto = CMS.Responses.StaticPageDto
  import { ExtendedComponent } from '~/store/plugins'
  import IFrameShopPlugin = Plugins.IFrameShopPlugin
  import ContentStaticPageDto = CMS.StaticPages.ContentStaticPageDto
  import SiteSections = CMS.SiteSections

  const cms = namespace('cms')
  const categories = namespace('categories')
  const products = namespace('products')
  const plugins = namespace('plugins')
  const routeStore = namespace('route')

  @Component({
    scrollToTop: true,
    middleware: ['isAuthenticatedOrPlatformOpened'],
    watchQuery: ['filtersQuery', 'rangeFiltersQuery', 'page', 'rows', 'sortCriteria', 'price'],
    transition(_to: any, from: any): string {
      if (from && (from.name.includes('search') || from.name === 'all')) {
        return 'no-transition'
      }
      return 'fade'
    },
    components: {
      OneSiteSectionsView: () => import('~/components/organisms/siteSections/OneSiteSectionsView.vue'),
      OneCatalogViewList: () => import('~/components/organisms/list/OneCatalogViewList.vue'),
      OneCmsGridRender: () => import('~/components/molecules/renders/OneCmsGridRender.vue'),
      OneExternalIframePlugin: () => import('~/components/molecules/plugins/OneExternalIframePlugin.vue'),
      Error: () => import('~/layouts/error.vue'),
    },
  })
  export default class GenericPage extends mixins<SeoMethodsMixin>(SeoMethodsMixin) {
    name: string = 'generic-page'

    @routeStore.State path: any;
    @cms.State(state => state.staticPages) staticPages!: Record<string, StaticPageDto>;
    @cms.State(state => state.siteSections) siteSections!: Record<string, SiteSections.SiteSection>;
    @categories.Getter getCategoryByUrl: any;
    @categories.Getter getSelectedCategory: any
    @categories.Getter getCategory: any
    @categories.State selectedCategoriesTree: any
    @products.Mutation('CLEAR_SELECTED_FILTERS') clearSelectedFilters: any;
    @plugins.Getter getRoutableComponentBySlug: any;

    notFoundError: any = {
      statusCode: 404,
      err: {},
    }

    availableComponents: Record<string, AsyncComponent> = {
      html: () => import('~/components/organisms/grid/shared/OneCmsDynamicComponent.vue'),
      staticHtml: () => import('~/components/organisms/grid/shared/OneCmsStaticComponent.vue'),
      image: () => import('~/components/molecules/images/OneCmsGridImageComponent.vue'),
      pluginSlot: () => import('~/components/organisms/grid/shared/OnePluginSlotComponent.vue'),
      link: () => import('~/components/molecules/links/OneCmsGridLinkComponent.vue'),
      productContainer: () => import('~/components/organisms/grid/content/OneCmsProductContainer.vue'),
      productsListing: () => import('~/components/organisms/grid/static/OneProductsListing.vue'),
      Category: () => import('~/components/organisms/grid/content/OneCmsCategoryOverview.vue'),
      TopProducts: () => import('~/components/organisms/grid/content/OneCmsTopProducts.vue'),
      StaticBanners: () => import('~/components/organisms/grid/content/OneCmsStaticBanners.vue'),
      carouselWithCategories: () => import('~/components/organisms/grid/content/OneCmsCategoriesListWithBanner.vue'),
      customizableProductsRotator: () => import('~/components/organisms/grid/content/OneCmsProductsRotator.vue'),
      carouselWithThumbnails: () => import('~/components/organisms/grid/content/OneCmsCarouselWithThumbnails.vue'),
      youtube: () => import('~/components/organisms/grid/shared/OneCmsYoutubeComponent.vue'),
      textEditor: () => import('~/components/organisms/grid/shared/OneCmsTextEditorComponent.vue'),
      codesScanner: () => import('~/components/organisms/shared/OneBarcodeScanner.vue'),
      gridArticle: () => import('~/components/organisms/grid/content/OneCmsArticle.vue'),
      productPack: () => import('~/components/organisms/grid/content/OneCmsProductPack.vue'),
      shopForm: () => import('~/components/organisms/grid/static/OneShopForm.vue'),
      staticPagesNodeList: () => import('~/components/organisms/grid/content/OneCmsStaticPagesNodeList.vue'),
    }

    mainCatalogPageClass = 'one-catalog-page'
    mainPluginPageClass = 'one-external-plugin-page'

    head() {
      return {
        title: this.structuredSeoTitle(DIVIDER.DOT, [this.filtersSeo, this.titleSeoPage]),
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.structuredSeoTitle(DIVIDER.DASH, this.descriptionSeoPage),
          },
          {
            hid: 'robots',
            name: 'robots',
            content: this.pageRobotsValue,
          },
        ],
      }
    }

    get staticPage(): StaticPageDto {
      return this.staticPages[this.path.substring(1)]
    }

    firstPathSegment(path: string): string {
      const normalizedPath = path.startsWith('/') ? path.substring(1) : path
      return normalizedPath.split('/')[0] || ''
    }

    get siteSection(): SiteSections.SiteSection {
      return this.siteSections[this.firstPathSegment(this.path)]
    }

    get pluginComponent(): ExtendedComponent<IFrameShopPlugin> {
      return this.getRoutableComponentBySlug(this.path.substring(1))
    }

    get titleSeoPage(): string | null {
      if (this.pluginComponent) { return this.pluginComponent.raw.page.title }
      if (this.staticPage) { return this.staticPage.seo.title }
      if (this.isCategoryView) {
        return this.getSelectedCategory?.seo?.title || this.getSelectedCategory?.name || null
      }
      return null
    }

    get hiddenH1Value() {
      if (this.staticPage) { return this.staticPage.seo.h1 }
    }

    get descriptionSeoPage(): Array<string> {
      if (this.staticPage) { return [this.staticPage.seo.description] }
      if (this.titleSeoPage) {
        const categoryDescription = this.getSelectedCategory?.seo?.description
        const defaultDescription = !categoryDescription && [this.defaultSeoData.description]
        return [
          this.structuredSeoTitle(DIVIDER.DASH, [categoryDescription || this.titleSeoPage, this.filtersSeo, defaultDescription]),
        ].filter(Boolean)
      } else {
        return [this.defaultSeoData.description]
      }
    }

    get pageRobotsValue(): string {
      if (this.staticPage) { return this.staticPage.seo.robots }
      return this.categoryRobotsSeo
    }

    get categoryRobotsSeo(): string {
      const categoryRobots = this.robotsSeo(this.getSelectedCategory?.seo)
      return this.isCategoryView && categoryRobots.length >= 2 ? categoryRobots : this.defaultSeoRobots
    }

    get isCategoryView(): boolean {
      return this.getCategoryByUrl(this.path)
    }

    get listingTitle(): string | undefined {
      const selectedCategory = this.getSelectedCategory && this.getSelectedCategory.name
      return selectedCategory && this.isCategoryView ? `${selectedCategory}` : undefined
    }

    destroyed() {
      if (this.isCategoryView) {
        this.$plugins.onUnmounted(this.mainCatalogPageClass)
      } else if (this.pluginComponent) {
        this.$plugins.onUnmounted(this.mainPluginPageClass)
      }
    }

    mounted() {
      if (this.isCategoryView) {
        this.$plugins.onMounted(this.mainCatalogPageClass, this)
      } else if (this.pluginComponent) {
        this.$plugins.onMounted(this.mainPluginPageClass, this)
      }
    }

    async fetch({
      app, store, route, redirect, error,
    }: any) {
      const firstPathSegment = (path: string): string => {
        const normalizedPath = path.startsWith('/') ? path.substring(1) : path
        return normalizedPath.split('/')[0] || ''
      }
      const handleGridStaticPage = (gridStaticPage?: ContentStaticPageDto | undefined) => {
        store.commit('layout/TOGGLE_BREADCRUMBS', false)
        if (gridStaticPage?.seo?.title && gridStaticPage?.slug) {
          store.commit('layout/SET_BREADCRUMBS', [{
            name: gridStaticPage.seo.title,
            link: `/${gridStaticPage.slug}`,
          }])
          store.commit('layout/TOGGLE_BREADCRUMBS', true)
        }
      }
      const handleCatalogPage = async (category: Category) => {
        const currentPriceType: string = store.getters['layout/priceType']
        const setCatalogLoading = () => {
          if (process.client) {
            app.wait.initialized && app.wait.start('catalog-loading')
          }
        }
        const clearCatalogLoading = () => {
          if (process.client) {
            setTimeout(() => {
              app.wait.initialized && app.wait.end('catalog-loading')
            }, 250)
          }
        }
        const addedPriceParam = (price: string | null) => {
          if (!(app.$utils.isAnonymousAndPurchaseDisabled || app.$utils.isB2BAndPlatformIsNotOpened) || !price) {
            store.commit('products/SET_PRICE', price)
          }
        }
        const redirectDeletePrice = () => {
          const { query } = route
          delete query.price
          addedPriceParam(null)
          return redirect(301, route.path, query)
        }
        const validPrice = () => {
          const { price } = route.query
          if (price) {
            const parsedPrice: [number, number] | null = app.$routes.parsePriceString(price)
            if (!parsedPrice) {
              addedPriceParam(null)
              return redirectDeletePrice()
            }
            const [min, max] = parsedPrice
            if (min <= max && price.includes(currentPriceType)) {
              return addedPriceParam(price)
            }
            return redirectDeletePrice()
          }
          addedPriceParam(null)
          return null
        }
        const fetchCatalogData = async () => {
          try {
            setCatalogLoading()
            const res: { ids: Array<string>, categorySelected: boolean } = await store.dispatch('products/fetchProducts', route.query)
            await store.dispatch('products/fetchFilters', route.query)
            if (res.categorySelected) {
              if (app.$utils.isPurchaseEnabledOrAuthenticated) {
                await Promise.all([
                  store.dispatch('stocks/fetchStocksForProductsAndWarehouses', {
                    skus: res.ids,
                    warehouses: store.state.stocks.warehouses.allIds,
                  }),
                  store.dispatch('products/fetchPricesForProducts', res.ids),
                ])
              }
            }
            clearCatalogLoading()
          } catch (error) {
            app.$logger.warn(error)
          }
        }
        const defaultRows = 15
        const defaultPage = 1
        const defaultSortCriteria = store.getters['products/getDefaultSortForCatalogBrowse']
        validPrice()
        await store.dispatch('categories/selectCategory', category.id)
        store.commit('layout/TOGGLE_BREADCRUMBS', true)
        store.commit('layout/SET_BREADCRUMBS', app.$routes.getCategoryBreadcrumbs())
        store.commit('products/SET_PAGE_SIZE', Number(route.query.rows || defaultRows))
        store.commit('products/SET_PAGE_NUMBER', Number(route.query.page || defaultPage))
        store.commit('products/SET_SORT_CRITERIA', route.query.sortCriteria || defaultSortCriteria)
        if (process.client) {
          fetchCatalogData()
        } else {
          await fetchCatalogData()
        }
        return Promise.resolve()
      }
      try {
        if (route.query.q) {
          return redirect(301, '/search', route.query)
        }
        const pluginComponent: ExtendedComponent<IFrameShopPlugin> | null = store.getters['plugins/getRoutableComponentBySlug'](route.path)
        if (pluginComponent) {
          return handleGridStaticPage()
        }
        const category: Category | null = store.getters['categories/getCategoryByUrl'](route.path)
        if (category) {
          return handleCatalogPage(category)
        }
        const gridStaticPage = route.path.substring(1) ? await store.dispatch('cms/fetchStaticPageBySlug', route.path.substring(1)) : null
        if (gridStaticPage) {
          handleGridStaticPage(gridStaticPage)
          const isSiteSection = store.getters['cms/siteSections']
          if (isSiteSection) {
            const sectionSlug = firstPathSegment(route.path)
            const section = isSiteSection[sectionSlug]
            if (section) {
              const crumbs = [
                {
                  name: section.name,
                  link: `/${section.slug}`,
                },
              ]
              const subSectionSlug = route.path.substring(1)
              if (subSectionSlug) {
                const subSection = section.items.find((item: SiteSections.SiteSubSection) => subSectionSlug.includes(item.slug))
                if (subSection) {
                  crumbs.push({
                    name: subSection.name,
                    link: `/${subSection.slug}`,
                  })
                }
              }
              store.commit('layout/SET_BREADCRUMBS', crumbs)
              store.commit('layout/TOGGLE_BREADCRUMBS', true)
            }
          }
          return Promise.resolve()
        }
        return store.commit('layout/TOGGLE_BREADCRUMBS', false)
      } catch (err: any) {
        return error({
          statusCode: err.response?.status,
          message: err.response?.data,
          err: err,
        })
      }
    }
  }
